import { USER_TYPES } from './user.model';
import { getEnumName, getEnumProperty } from '@/utils/util';

const WALLET_GENERAL_LABEL = 'LocumWallet™';

export const WALLET_TYPES = {
	PROFESSIONAL: { name: 'LocumBucks™', value: 1, img: require('@/assets/images/LocumBucks.jpg') },
	COMPANY: { name: 'LocumCredits™', value: 2, img: require('@/assets/images/LocumCredits.jpg') },
};

export const WALLET_TRANSACTION_TYPES = {
	ADDITION: 1,
	DEDUCTION: 2,
};

export class Wallet {
	constructor({ type = WALLET_TYPES.PROFESSIONAL.value, transactions = [] } = {}) {
		this.type = type;
		this.transactions = transactions
			.map(x => new WalletTransaction(x))
			.sort((a, b) => {
				if (a.date !== b.date) {
					return new Date(b.date) - new Date(a.date);
				} else {
					return new Date(b.createdAt) - new Date(a.createdAt);
				}
			});
	}

	static getNameByType(type) {
		return getEnumName(this.TYPES, type);
	}

	static getImageByType(type) {
		return getEnumProperty(this.TYPES, type, 'img');
	}

	calcTheoreticalBalance({ type, amount }) {
		return this.calcValue(type, this.total, amount);
	}

	static getWalletLabelByUserType(userType) {
		if (userType === USER_TYPES.PROFESSIONAL) {
			return this.TYPES.PROFESSIONAL.name;
		}
		if (userType === USER_TYPES.COMPANY) {
			return this.TYPES.COMPANY.name;
		}
		return '';
	}

	get walletTrackingCode() {
		if (this.type === WALLET_TYPES.PROFESSIONAL.value) {
			return 'Professional_Wallet_Open';
		}
		if (this.type === WALLET_TYPES.COMPANY.value) {
			return 'Company_Wallet_Open';
		}
		return 'Wallet_Open';
	}

	get label() {
		return Wallet.getNameByType(this.type);
	}

	get image() {
		return Wallet.getImageByType(this.type);
	}

	get total() {
		let total = 0;
		for (const transaction of this.transactions) {
			total = this.calcValue(transaction.type, total, transaction.amount);
		}
		return total;
	}

	get balanceMap() {
		const transactions = this.transactions.slice().reverse();
		const balanceMap = new Map();

		let balance = 0;
		for (const transaction of transactions) {
			if (!transaction.id) continue;
			balance = this.calcValue(transaction.type, balance, transaction.amount);
			balanceMap.set(transaction.id, balance);
		}

		return balanceMap;
	}

	static get TYPES() {
		return WALLET_TYPES;
	}

	get generalLabel() {
		return WALLET_GENERAL_LABEL;
	}

	getTransactionBalance(transactionId) {
		if (!this.balanceMap.has(transactionId)) return 0;

		return this.balanceMap.get(transactionId);
	}

	calcValue(type, total, amount) {
		if (type === WalletTransaction.TYPES.ADDITION) total += +amount;
		if (type === WalletTransaction.TYPES.DEDUCTION) total -= +amount;
		return total;
	}

	sortDesc() {
		this.transactions.sort((a, b) => {
			if (a.date !== b.date) {
				return new Date(b.date) - new Date(a.date);
			} else {
				return new Date(b.createdAt) - new Date(a.createdAt);
			}
		});
	}

	sortAsc() {
		this.transactions.sort((a, b) => {
			if (a.date !== b.date) {
				return new Date(a.date) - new Date(b.date);
			} else {
				return new Date(a.createdAt) - new Date(b.createdAt);
			}
		});
	}
}

export class WalletTransaction {
	constructor({ id = '', date = '', amount = 0, type = WALLET_TRANSACTION_TYPES.ADDITION, note = '', reference = '', referenceLink = '', createdAt = '' } = {}) {
		(this.id = id), (this.date = date);
		this.amount = amount;
		this.type = type;
		this.note = note;
		this.reference = reference;
		this.referenceLink = referenceLink;
		this.createdAt = createdAt;
	}

	get typeColor() {
		if (this.type === WalletTransaction.TYPES.ADDITION) return 'green';
		if (this.type === WalletTransaction.TYPES.DEDUCTION) return 'red';
		return '';
	}

	get typeSymbol() {
		if (this.type === WalletTransaction.TYPES.ADDITION) return 'mdi-plus';
		if (this.type === WalletTransaction.TYPES.DEDUCTION) return 'mdi-minus';
		return '';
	}

	static get TYPES() {
		return WALLET_TRANSACTION_TYPES;
	}

	toJSON() {
		return {
			id: this.id,
			date: this.date,
			amount: this.amount,
			type: this.type,
			note: this.note,
			reference: this.reference,
			createdAt: this.createdAt,
		};
	}
}

export default Wallet;
